/**
 * 数据地图：全库 53 张表的行数与体积。
 *
 * 行数取 `pg_stat_user_tables.n_live_tup`（统计估算）而不是逐表 count(*)——
 * 这个页面要的是数量级和「哪些表还是空的」，不是精确值。
 *
 * 空表不是缺陷：交易、任务、向量索引等能力尚未实现，结构先立起来。
 */

import { Alert, Card, Space, Table, Tag, Typography } from "antd";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../services/api";
import { fmtBytes, fmtNumber } from "../../components/format";

interface TableRow {
  table: string;
  rowEstimate: number;
  totalBytes: number;
  indexBytes: number;
}

export function TableCensusPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["overview", "tables"],
    queryFn: () => api.get<TableRow[]>("/api/v1/admin/overview/tables"),
  });

  if (error) return <Alert type="error" showIcon message={(error as Error).message} />;

  const rows = data ?? [];
  const empty = rows.filter((r) => r.rowEstimate === 0);
  const totalBytes = rows.reduce((n, r) => n + r.totalBytes, 0);

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message={`${rows.length} 张表 · 共 ${fmtBytes(totalBytes)} · 其中 ${empty.length} 张为空`}
        description="空表可能只是尚未产生数据，不应直接判定为数据缺失。"
      />
      <Card styles={{ body: { padding: 12 } }}>
        <Table<TableRow>
          size="small"
          rowKey="table"
          loading={isLoading}
          dataSource={rows}
          pagination={{ pageSize: 60, showSizeChanger: false }}
          columns={[
            {
              title: "表",
              dataIndex: "table",
              render: (v: string, r) => (
                <Space>
                  <Typography.Text strong={r.rowEstimate > 0}>{v}</Typography.Text>
                  {r.rowEstimate === 0 && <Tag>空</Tag>}
                </Space>
              ),
            },
            {
              title: "行数（估算）",
              dataIndex: "rowEstimate",
              align: "right",
              sorter: (a, b) => a.rowEstimate - b.rowEstimate,
              defaultSortOrder: "descend",
              render: fmtNumber,
            },
            {
              title: "总体积",
              dataIndex: "totalBytes",
              align: "right",
              sorter: (a, b) => a.totalBytes - b.totalBytes,
              render: fmtBytes,
            },
            {
              title: "其中索引",
              dataIndex: "indexBytes",
              align: "right",
              sorter: (a, b) => a.indexBytes - b.indexBytes,
              render: fmtBytes,
            },
          ]}
        />
      </Card>
    </Space>
  );
}
